
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import * as ContestServices from '@/services/ContestServices';

/** The fields of the Wedstrijd page this dialog reads; the answers themselves are never saved in the page. */
interface ContestContent {
  opens?: unknown;
  closes?: unknown;
  question?: unknown;
  options?: unknown;
  tiebreaker?: unknown;
}

const DATE = /^\d{4}-\d{2}-\d{2}$/;
const NUMBER = /^\d{1,12}([.,]\d{1,4})?$/;

/**
 * "Inzendingen" on the Wedstrijd page: asks the right answers and downloads the entries ranked by the API.
 * The answers are asked here instead of stored in the CMS, because the page content is public.
 */
@Component
export default class ContestEntriesDialog extends Vue {
  @Prop({ required: true }) readonly content!: ContestContent;

  open = false;
  loading = false;
  answer = '';
  tiebreaker = '';
  error = '';
  /** The number of entries, for the button and the schiftingsvraag; null until fetched or when it failed. */
  count: number | null = null;
  /** The tiebreaker as filled in from the count, so a value the editor typed is not overwritten. */
  prefilled = '';

  /**
   * The usual schiftingsvraag is "how many take part?", and the system knows: fill in the count.
   * The question is free text, so the editor can still type another answer.
   */
  @Watch('contestId', { immediate: true })
  async loadCount(): Promise<void> {
    this.count = null;
    if (!this.contestId) return;
    const contestId = this.contestId;
    try {
      const count = await ContestServices.CountEntries(contestId);
      // The start date may have changed while this was on its way.
      if (contestId === this.contestId) this.count = count;
    } catch (error) {
      // Without a count the button shows no number and the editor fills in the schiftingsvraag.
    }
  }

  /** Counted again on opening, as entries keep coming in while the page is open. */
  @Watch('open')
  async onOpen(open: boolean): Promise<void> {
    if (!open) return;
    await this.loadCount();
    if (this.count === null) return;
    if (!this.tiebreaker.trim() || this.tiebreaker === this.prefilled) {
      this.tiebreaker = String(this.count);
      this.prefilled = this.tiebreaker;
    }
  }

  get tiebreakerHint(): string {
    return this.count !== null && this.tiebreaker === this.prefilled
      ? 'Ingevuld met het aantal inzendingen. Vraagt de schiftingsvraag iets anders, vul dan het juiste getal in.'
      : 'Het juiste antwoord, een getal';
  }

  get opens(): string {
    return typeof this.content.opens === 'string' && DATE.test(this.content.opens) ? this.content.opens : '';
  }

  get closes(): string {
    return typeof this.content.closes === 'string' && DATE.test(this.content.closes) ? this.content.closes : '';
  }

  /** The site never opens such a contest (contestIsOpen), so warn while the editor is on the page. */
  get datesSwapped(): boolean {
    return Boolean(this.opens && this.closes) && this.closes < this.opens;
  }

  /** Same id as the site stores the entries under (contestId in web/src/content/rubrieken.format.ts). */
  get contestId(): string {
    return this.opens ? `wedstrijd-${this.opens}` : '';
  }

  get opensLabel(): string {
    return this.opens ? new Date(`${this.opens}T12:00:00Z`).toLocaleDateString('nl-BE', { day: 'numeric', month: 'long', year: 'numeric' }) : '';
  }

  get question(): string {
    return typeof this.content.question === 'string' ? this.content.question.trim() : '';
  }

  get tiebreakerLabel(): string {
    return typeof this.content.tiebreaker === 'string' ? this.content.tiebreaker : '';
  }

  /**
   * The answers to choose from, as the site shows them (contestChoices in web/src/content/rubrieken.format.ts).
   * The site drops the whole list when one answer is not a filled-in text or there are more than 4 (contestSchema), so this does too.
   */
  get choices(): string[] {
    const options = this.content.options;
    if (!Array.isArray(options) || options.length > 4 || !options.every((option) => typeof option === 'string' && option.length > 0)) return [];
    const choices = [...new Set((options as string[]).map((option) => option.trim()).filter(Boolean))];
    return this.question && choices.length >= 2 ? choices : [];
  }

  get ready(): boolean {
    return NUMBER.test(this.tiebreaker.trim()) && (!this.choices.length || Boolean(this.answer));
  }

  async download(): Promise<void> {
    this.error = '';
    this.loading = true;
    try {
      const blob = await ContestServices.DownloadEntries(this.contestId, this.tiebreaker.trim(), this.choices.length ? this.answer : '');
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${this.contestId}.csv`;
      link.click();
      // Some browsers start the download after click() returns; revoking straight away can cancel it.
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      this.open = false;
    } catch (error) {
      this.error = 'Downloaden is mislukt. Probeer het opnieuw.';
    }
    this.loading = false;
  }
}
